import React, { useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import { useStyles } from "components/layout/CommonStyles";
import { useTranslation } from "react-i18next";
import CloseIcon from "@material-ui/icons/Close";
import Buttons from "components/buttons/Pressed";
import Grid from "@material-ui/core/Grid";
import Input from "components/inputs/Input";
import { GetNumberOfDays } from "utils/dates/GetNumberOfDays";
import { useMutationUpdateScholarshipRecess } from "../../../hooks/DataFetching/HR/ScholarshipRecesses/useMutationUpdateScholarshipRecess";
import { useMutationCreateScholarshipRecess } from "../../../hooks/DataFetching/HR/ScholarshipRecesses/useMutationCreateScholarshipRecess";
import { useFormData } from "../../../hooks/useFormData";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import DateUtils from "utils/dates/DateUtils";
import { getAuthLocalStorage } from "utils/requests/auth";
import { dateValidation } from "utils/validation/DateValidation";

const DialogViewScholarshipRecess = ({
  open,
  setOpen,
  context,
  item,
  itemList,
}) => {
  var auth = getAuthLocalStorage();

  const classes = useStyles();
  const [t] = useTranslation();
  const { formData, setFieldValue, setFieldError } = useFormData(
    "operator",
    "note",
    "startDate",
    "endDate"
  );

  useEffect(() => {
    const setDefaultValues = () => {
      if (item === null || item === undefined) return;

      setFieldValue("operator", item.operator);
      setFieldValue("note", item.note);
      setFieldValue(
        "startDate",
        item.startDate ? DateUtils.parse(item.startDate) : null
      );
      setFieldValue(
        "endDate",
        item.endDate ? DateUtils.parse(item.endDate) : null
      );
    };

    setDefaultValues();
  }, [item, setFieldValue]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleInputOnChange = (name, newValue) => {
    setFieldValue(name, newValue);
    setFieldError(name, "");
  };

  const getDaysInRecess = (startDate, endDate) => {
    if (dateValidation(startDate) && dateValidation(endDate)) {
      const dayCount = GetNumberOfDays(startDate, endDate);
      const dayString = dayCount > 1 ? t("GENERAL.days") : t("GENERAL.day");
      return `${dayCount} ${dayString}`;
    }
    return "-";
  };

  const verifySubmit = () => {
    let canSubmit = true;

    setFieldError("startDate", "");
    setFieldError("endDate", "");

    if (!dateValidation(formData.startDate.value)) {
      canSubmit = false;
      setFieldError("startDate", t("GENERAL.invalidDate"));
    }

    if (!dateValidation(formData.endDate.value)) {
      canSubmit = false;
      setFieldError("endDate", t("GENERAL.invalidDate"));
    }

    if (!formData.startDate.value) {
      canSubmit = false;
      setFieldError("startDate", t("GENERAL.requiredField"));
    }

    if (!formData.endDate.value) {
      canSubmit = false;
      setFieldError("endDate", t("GENERAL.requiredField"));
    }

    if (
      formData.endDate.value &&
      formData.startDate.value &&
      formData.startDate.value.getTime() > formData.endDate.value.getTime()
    ) {
      canSubmit = false;
      setFieldError("startDate", t("GENERAL.initialDateBiggerthanEndDate"));
    }

    const itemValue = context === "edit" ? itemList : item;

    itemValue.recessHistory.forEach(recess => {
      if (recess.deletedAt === null && recess.id !== item.id) {
        const recessStartDateTime = DateUtils.parse(recess.startDate).getTime();
        const recessEndDateTime = DateUtils.parse(recess.endDate).getTime();
        const formDataStartDateTime = formData.startDate.value.getTime();
        const formDataEndDateTime = formData.endDate.value.getTime();

        if (
          recessStartDateTime >= formDataStartDateTime &&
          recessEndDateTime <= formDataEndDateTime
        ) {
          canSubmit = false;
          setFieldError("endDate", t("HR.breakPeriodAlreadyRegistered"));
          setFieldError("startDate", t("HR.breakPeriodAlreadyRegistered"));
        }

        if (
          recessStartDateTime <= formDataEndDateTime &&
          formDataEndDateTime <= recessEndDateTime
        ) {
          canSubmit = false;
          setFieldError("endDate", t("HR.breakPeriodAlreadyRegistered"));
        }

        if (
          recessStartDateTime <= formDataStartDateTime &&
          formDataStartDateTime <= recessEndDateTime
        ) {
          canSubmit = false;
          setFieldError("startDate", t("HR.breakPeriodAlreadyRegistered"));
        }
      }
    });

    return canSubmit;
  };

  const mutateUpdate = useMutationUpdateScholarshipRecess(item.id);
  const mutateCreate = useMutationCreateScholarshipRecess();

  const handleSubmit = () => {
    if (verifySubmit()) {
      if (context === "edit") {
        const payload = {
          startDate: formData.startDate.value,
          endDate: formData.endDate.value,
          note: formData.note.value,
        };

        mutateUpdate.mutate(payload, {
          onSuccess: () => {
            handleClose();
          },
        });
      } else {
        const payload = {
          contractId: item.contractId,
          startDate: formData.startDate.value,
          endDate: formData.endDate.value,
          operatorId: auth.id,
          note: formData.note.value,
        };

        mutateCreate.mutate(payload, {
          onSuccess: () => {
            handleClose();
          },
        });
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle className={classes.Title} id="form-dialog-title">
        <b>
          {`${
            context === "view"
              ? t("GENERAL.view")
              : context === "edit"
              ? t("GENERAL.edit")
              : t("GENERAL.add")
          } ${t("HR.recessPeriod")}`}
        </b>
        <IconButton
          style={{ float: "right", marginTop: -10, marginRight: -17 }}
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container item spacing={2} md={12} lg={12}>
          <Grid item xs={6} sm={6}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                disabled={context === "view"}
                error={formData.startDate.error}
                helperText={formData.startDate.errorMsg}
                className={classes.identificationDatePicker}
                disableToolbar
                autoOk={true}
                variant="inline"
                format="dd/MM/yyyy"
                id="validity"
                margin="normal"
                invalidDateMessage=""
                label={t("GENERAL.startDate") + "*"}
                KeyboardButtonProps={{ "aria-label": "change date" }}
                value={formData.startDate.value}
                onChange={newDate => handleInputOnChange("startDate", newDate)}
                inputProps={{ autoComplete: "off" }}
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item xs={6} sm={6}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                disabled={context === "view"}
                error={formData.endDate.error}
                helperText={formData.endDate.errorMsg}
                className={classes.identificationDatePicker}
                disableToolbar
                autoOk={true}
                variant="inline"
                format="dd/MM/yyyy"
                id="validity"
                margin="normal"
                invalidDateMessage=""
                label={t("GENERAL.endDate") + "*"}
                KeyboardButtonProps={{ "aria-label": "change date" }}
                value={formData.endDate.value}
                onChange={newDate => handleInputOnChange("endDate", newDate)}
                inputProps={{ autoComplete: "off" }}
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item xs={12} sm={12}>
            <Input
              disabled
              context={context}
              label={t("HR.daysInRecess")}
              value={getDaysInRecess(
                formData.startDate.value,
                formData.endDate.value
              )}
              valueName={"daysInRecess"}
              handleChange={handleInputOnChange}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <Input
              context={context}
              label={"GENERAL.observation"}
              value={formData.note.value}
              valueName={"note"}
              rows={5}
              maxLength={250}
              handleChange={handleInputOnChange}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Buttons
          onClick={handleClose}
          tipo="BN-noback"
          conteudo={
            context === "view"
              ? t("GENERAL.close").toUpperCase()
              : t("GENERAL.cancel").toUpperCase()
          }
        />
        {context === "view" ? null : (
          <Buttons
            onClick={handleSubmit}
            tipo="BN-blue"
            conteudo={
              context === "edit"
                ? t("GENERAL.save").toUpperCase()
                : t("GENERAL.add").toUpperCase()
            }
          />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DialogViewScholarshipRecess;
